import { useState, useRef, useEffect } from "react"
import { Check, ChevronsUpDown, Plus, X } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { PromptCategory } from "@/lib/api"

interface CategoryComboboxProps {
  categories: PromptCategory[]
  value: string | null
  onChange: (categoryId: string | null) => void
  onCreateCategory: (name: string) => Promise<PromptCategory>
  disabled?: boolean
}

export function CategoryCombobox({
  categories,
  value,
  onChange,
  onCreateCategory,
  disabled = false,
}: CategoryComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedCategory = categories.find((c) => c.id === value)

  const filtered = search.trim()
    ? categories.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
      )
    : categories

  const exactMatch = categories.some(
    (c) => c.name.toLowerCase() === search.trim().toLowerCase()
  )
  const showCreate = search.trim().length > 0 && !exactMatch

  useEffect(() => {
    if (open) {
      // Small delay to let popover mount
      setTimeout(() => inputRef.current?.focus(), 0)
    } else {
      setSearch("")
    }
  }, [open])

  const handleSelect = (categoryId: string) => {
    onChange(categoryId)
    setOpen(false)
  }

  const handleCreate = async () => {
    if (!search.trim() || isCreating) return
    setIsCreating(true)
    try {
      const category = await onCreateCategory(search.trim())
      onChange(category.id)
      setOpen(false)
    } finally {
      setIsCreating(false)
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-9"
          disabled={disabled}
        >
          <span className={cn(!selectedCategory && "text-muted-foreground")}>
            {selectedCategory?.name ?? "Select category..."}
          </span>
          <div className="flex items-center gap-1">
            {selectedCategory && (
              <X
                className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                onClick={handleClear}
              />
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="p-2">
          <input
            ref={inputRef}
            placeholder="Search or create..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && showCreate) {
                e.preventDefault()
                void handleCreate()
              }
            }}
            className="w-full px-2 py-1.5 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div className="max-h-[200px] overflow-y-auto border-t border-border/50">
          {filtered.length === 0 && !showCreate && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              No categories found
            </div>
          )}
          {filtered.map((category) => (
            <button
              key={category.id}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
              onClick={() => handleSelect(category.id)}
            >
              <Check
                className={cn(
                  "h-4 w-4 flex-shrink-0",
                  value === category.id ? "opacity-100" : "opacity-0"
                )}
              />
              <span className="truncate">{category.name}</span>
            </button>
          ))}
          {showCreate && (
            <>
              {filtered.length > 0 && (
                <div className="border-t border-border/50" />
              )}
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary font-medium hover:bg-accent transition-colors disabled:opacity-50"
                onClick={() => void handleCreate()}
                disabled={isCreating}
              >
                <Plus className="h-4 w-4 flex-shrink-0" />
                <span>Create &ldquo;{search.trim()}&rdquo;</span>
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
